import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import FooterSection from "../home/components/FooterSection";
import { useUser } from "../../contexts/UserContext";
import MyRoutesSection from "./sections/MyRoutesSection";
import ProfileEditSection from "./sections/ProfileEditSection";
import MyCouponsSection from "./sections/MyCouponsSection";
import VisitedVenuesSection from "./sections/VisitedVenuesSection";
import { supabase } from "../../lib/supabase";
import MessagesSection from "./sections/MessagesSection";

type Tab = "routes" | "visited" | "coupons" | "profile" | "messages";

const TABS: { key: Tab; icon: string; label: string }[] = [
  { key: "routes",   icon: "ri-route-line",      label: "Rotalarım" },
  { key: "visited",  icon: "ri-map-pin-line",     label: "Gittiklerim" },
  { key: "coupons",  icon: "ri-coupon-line",      label: "Kuponlarım" },
  { key: "messages", icon: "ri-message-3-line",   label: "Mesajlar" },
  { key: "profile",  icon: "ri-settings-3-line",  label: "Ayarlar" },
];

interface ProfileStats {
  routes: number;
  visited: number;
  coupons: number;
}

function useProfileStats(userId: string | undefined) {
  const [stats, setStats] = useState<ProfileStats>({ routes: 0, visited: 0, coupons: 0 });

  useEffect(() => {
    if (!userId) return;

    const fetchStats = async () => {
      const [routesRes, visitedRes, couponsRes] = await Promise.all([
        supabase.from("user_routes").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("visited_venues").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("user_coupons").select("id", { count: "exact", head: true }).eq("user_id", userId),
      ]);
      setStats({
        routes: routesRes.count ?? 0,
        visited: visitedRes.count ?? 0,
        coupons: couponsRes.count ?? 0,
      });
    };

    fetchStats();
  }, [userId]);

  return stats;
}

function useUnreadMessages(userId: string | undefined) {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!userId) return;
    const fetch = () => {
      supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("receiver_id", userId)
        .is("read_at", null)
        .then(({ count }) => setUnread(count ?? 0));
    };
    fetch();
    const ch = supabase
      .channel(`unread:${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "messages", filter: `receiver_id=eq.${userId}` }, fetch)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId]);

  return unread;
}

// ─── Global Loading Screen ────────────────────────────────────────────────────

function GlobalLoading({ timedOut, onRetry }: { timedOut: boolean; onRetry: () => void }) {
  if (timedOut) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
        <div className="w-12 h-12 flex items-center justify-center text-stone-400">
          <i className="ri-wifi-off-line text-3xl" />
        </div>
        <p className="text-stone-700 font-semibold text-center">Bağlantı kurulamıyor</p>
        <p className="text-stone-400 text-sm text-center max-w-xs">
          Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.
        </p>
        <button
          onClick={onRetry}
          className="px-5 py-2.5 bg-emerald-700 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-emerald-800 transition-colors"
        >
          <i className="ri-refresh-line mr-2" />
          Yenile
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-stone-400 text-sm">Oturum kontrol ediliyor...</p>
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export default function AccountPage() {
  const { user, logout, loading } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("routes");
  const [timedOut, setTimedOut] = useState(false);
  const stats = useProfileStats(user?.id);
  const unreadMessages = useUnreadMessages(user?.id);

  // Read ?tab= from URL
  useEffect(() => {
    const paramTab = searchParams.get("tab") as Tab | null;
    if (paramTab && ["routes", "visited", "coupons", "messages", "profile"].includes(paramTab)) {
      setTab(paramTab);
    }
  }, [searchParams]);

  // Timeout guard for initial loading
  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const t = setTimeout(() => {
      setTimedOut(true);
      console.warn("[AccountPage] Loading timed out after 5s");
    }, 5000);
    return () => clearTimeout(t);
  }, [loading]);

  // ── Phase 1: auth check ───────────────────────────────────────────────────
  if (loading) {
    return (
      <GlobalLoading
        timedOut={timedOut}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // ── Phase 2: not logged in → redirect to login (handled by ProtectedRoute, but just in case)
  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-stone-400 text-sm">Yönlendiriliyor...</p>
        </div>
      </div>
    );
  }

  // ── Phase 3: logged in → show dashboard ──────────────────────────────────
  const initials = user.displayName.charAt(0).toUpperCase();
  const joinDate = new Date(user.joinedAt).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "long",
  });

  const TAB_ICONS: Record<Tab, string> = {
    routes: "ri-route-line",
    visited: "ri-map-pin-line",
    coupons: "ri-coupon-line",
    profile: "ri-settings-3-line",
    messages: "ri-message-3-line",
  };

  const STAT_BADGES = [
    { icon: "ri-route-line", label: "Rota", value: stats.routes, tab: "routes" as Tab, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { icon: "ri-map-pin-2-line", label: "Mekan", value: stats.visited, tab: "visited" as Tab, color: "text-amber-700 bg-amber-50 border-amber-200" },
    { icon: "ri-coupon-line", label: "Kupon", value: stats.coupons, tab: "coupons" as Tab, color: "text-violet-700 bg-violet-50 border-violet-200" },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* ── Banner + Profile Card ── */}
      <div className="relative pt-16">
        {/* Green gradient banner — uses user's banner if set */}
        <div className="h-36 md:h-44 relative overflow-hidden">
          {user.bannerUrl ? (
            <img
              src={user.bannerUrl}
              alt="Profil kapak"
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-800 via-emerald-700 to-stone-800 relative">
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
            </div>
          )}
        </div>

        {/* Profile card overlapping banner */}
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-white border border-stone-200 rounded-2xl px-5 md:px-7 pb-5 mb-6 -mt-8">
            {/* Avatar row */}
            <div className="flex items-end justify-between -mt-10 mb-4">
              <div className="flex-shrink-0">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.displayName}
                    className="w-20 h-20 rounded-2xl object-cover object-top border-4 border-white shadow-sm"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center border-4 border-white shadow-sm">
                    <span className="text-white font-bold text-3xl">{initials}</span>
                  </div>
                )}
              </div>

              {/* Logout */}
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="inline-flex items-center gap-2 px-4 py-2 border border-stone-200 rounded-xl text-sm text-stone-500 hover:bg-red-50 hover:text-red-500 hover:border-red-200 cursor-pointer transition-colors whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-logout-box-line text-sm" />
                Çıkış Yap
              </button>
            </div>

            {/* Info */}
            <div className="mb-5">
              <h1 className="font-bold text-stone-900 text-xl leading-tight">
                {user.displayName}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                <p className="text-stone-500 text-sm">{user.email}</p>
                {user.phone && (
                  <p className="text-stone-400 text-xs">+90 {user.phone}</p>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="inline-flex items-center gap-1 text-xs text-stone-400">
                  <i className="ri-calendar-line" />
                  {joinDate} üyesi
                </span>
                <RouterLink
                  to={`/profile/${user.username}`}
                  className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline cursor-pointer whitespace-nowrap font-medium"
                >
                  <i className="ri-external-link-line" />
                  @{user.username}
                </RouterLink>
              </div>
            </div>

            {/* ── Stat Badges ── */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-stone-100">
              {STAT_BADGES.map(({ icon, label, value, tab: badgeTab, color }) => (
                <button
                  key={badgeTab}
                  type="button"
                  onClick={() => setTab(badgeTab)}
                  className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm font-semibold cursor-pointer transition-all hover:scale-105 active:scale-100 whitespace-nowrap ${color}`}
                >
                  <span className={`w-7 h-7 flex items-center justify-center rounded-lg ${color.split(" ").filter(c => c.startsWith("bg-")).join(" ")}`}>
                    <i className={`${icon} text-base`} />
                  </span>
                  <span className="text-xl font-bold leading-none">{value}</span>
                  <span className="font-medium opacity-80">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-0 border-b border-stone-200 mb-8 overflow-x-auto">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  tab === key
                    ? "border-emerald-600 text-emerald-700"
                    : "border-transparent text-stone-500 hover:text-stone-800 hover:border-stone-300"
                }`}
              >
                <i className={`${TAB_ICONS[key]} text-base`} />
                <span>{label}</span>
                {key === "messages" && unreadMessages > 0 && (
                  <span className="ml-0.5 min-w-[18px] h-[18px] px-1 bg-emerald-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadMessages}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="pb-20">
            {tab === "routes"   && <MyRoutesSection userId={user.id} username={user.username} />}
            {tab === "visited"  && <VisitedVenuesSection userId={user.id} />}
            {tab === "coupons"  && <MyCouponsSection userId={user.id} />}
            {tab === "messages" && <MessagesSection />}
            {tab === "profile"  && <ProfileEditSection />}
          </div>
        </div>
      </div>

      <FooterSection />
    </div>
  );
}